// client/src/utils/scopeHeaders.js
//
// ONE ANSWER TO "WHICH SCHOOL, PHASE AND YEAR AM I IN".
//
// This is the web's copy of `scopeHeaders` from @ameca/core/scope, which the
// phone has always had, and it exists because the web did not: every page that
// wanted these headers built them inline, and forty-odd copies had drifted.
//
// The drift was not cosmetic. Two of them disagreed inside a single file —
// TeacherMarksPage resolved the school id as `schoolId || activeSchoolId ||
// scopeSchoolId` when loading sequence status, and as `activeSchoolId ||
// scopeSchoolId || schoolId` when loading evaluations and when SAVING marks.
// The academic year is stored under a school-namespaced key, so when those two
// values differ the same page reads one year and writes to another.
//
// Two rules, both taken from the phone and from fetchJson's own injection:
//
//   SCHOOL ID: activeSchoolId, then scopeSchoolId, then schoolId. `activeSchoolId`
//   is the one the scope switcher writes, so it has to win; putting the static
//   `schoolId` first pins a page to the signed-in user's home school.
//
//   ACADEMIC YEAR: the namespaced key, FALLING BACK to the un-namespaced one.
//   They are a fallback chain, not alternatives. Reading only the namespaced key
//   yields an empty year whenever the school id has not been resolved yet, and
//   an empty year means the request goes out unscoped.
//
// Headers are omitted rather than sent empty. fetchJson fills in anything absent
// from its own resolution, so an omitted header self-heals while an empty one
// is still a value and can suppress that.

const get = (k) => {
  try { return localStorage.getItem(k); } catch (_) { return null; }
};

/** The active school id, resolved the way fetchJson and the phone resolve it. */
export function activeSchoolId() {
  return get('activeSchoolId') || get('scopeSchoolId') || get('schoolId') || '';
}

/** The academic year in scope, namespaced key first, global key as fallback. */
export function activeAcademicYearId() {
  const schoolId = activeSchoolId();
  return get(schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId')
    || get('currentAcademicYearId')
    || '';
}

/** The active phase code ('sec' / 'nur_pri'). */
export function activePhaseCode() {
  return get('schoolAdmin_activePhase') || get('activePhase') || '';
}

/** The active phase id, which is a hint — see the note in derivePrivileges. */
export function activePhaseId() {
  return get('schoolAdmin_activePhaseId') || get('activePhaseId') || '';
}

/**
 * Scope headers for a request, matching @ameca/core/scope's scopeHeaders().
 *
 * Content-Type is deliberately NOT set here, unlike the phone's version: the
 * web's fetchJson sets it itself when there is a JSON body, and forcing it onto
 * a GET or a FormData upload would be wrong.
 */
export default function getScopeHeaders() {
  const schoolId = activeSchoolId();
  const phaseCode = activePhaseCode();
  const phaseId = activePhaseId();
  const ayId = activeAcademicYearId();

  return {
    ...(schoolId ? { 'X-Scope-School-Id': schoolId } : {}),
    ...(phaseCode ? { 'X-Active-Phase': phaseCode } : {}),
    ...(ayId ? { 'X-Academic-Year-Id': ayId } : {}),
    ...(phaseId ? { 'X-Active-Phase-Id': phaseId } : {}),
  };
}
